import { useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { markAnnouncementRead } from '../services/systemAnnouncementService'
import { shouldShowEffectiveDates } from '../types/roleNotification'
import type { SystemAnnouncementDetailModalProps } from '../types'
import { normalizeAnnouncementDescriptionHtml } from '../utils/normalizeAnnouncementHtml'
import '../styles/announcementHtml.css'

/**
 * Resident/staff acknowledgement popup — Angular
 * `SystemAnnouncementDetailComponent` ("Remind me Later" / "Okay, I've Got It").
 *
 * Data comes from `current_role.notification` (set by POST /api/change_role),
 * not from a separate fetch. "Okay, I've Got It" POSTs `/api/notification/{sa_id}`.
 */
export function SystemAnnouncementDetailModal({
  isOpen,
  announcement,
  onDismiss,
  onAcknowledged,
}: SystemAnnouncementDetailModalProps) {
  const [submitting, setSubmitting] = useState(false)

  const descriptionHtml = announcement?.announcement_description
    ? normalizeAnnouncementDescriptionHtml(announcement.announcement_description)
    : ''

  const handleRemindLater = () => {
    if (submitting) return
    onDismiss()
  }

  const handleAcknowledge = async () => {
    if (!announcement || submitting) return
    setSubmitting(true)
    try {
      await markAnnouncementRead(announcement.sa_id)
      showSuccess('Data Updated Successfully')
      if (onAcknowledged) onAcknowledged(announcement.sa_id)
      else onDismiss()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={isOpen && Boolean(announcement)}
      title="System Announcement"
      onClose={handleRemindLater}
      maxWidth="max-w-640"
      bodyScrollOnly
      headerVariant="centeredTitle"
      panelDataTestId="system-announcement-detail-modal"
      footer={
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          data-testid="system-announcement-detail-footer"
        >
          <button
            type="button"
            onClick={handleRemindLater}
            disabled={submitting}
            className={cn(primaryEntityActionButtonClass, 'min-w-[140px]')}
            data-testid="system-announcement-remind-later"
          >
            Remind me Later
          </button>
          <button
            type="button"
            onClick={() => void handleAcknowledge()}
            disabled={submitting}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[140px] border-[#1597ff] text-[#1597ff]')}
            data-testid="system-announcement-acknowledge"
          >
            {submitting ? 'Saving...' : "Okay, I've Got It"}
          </button>
        </div>
      }
    >
      {announcement && (
        <div className="space-y-4 p-1 text-sm text-slate-800" data-testid="system-announcement-detail-body">
          <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1">
            <h6 className="text-sm font-semibold text-slate-900">Topic:</h6>
            <p className="text-sm text-slate-800">{announcement.announcement_topic}</p>
          </div>

          {shouldShowEffectiveDates(announcement) && (
            <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1">
              <h6 className="text-sm font-semibold text-slate-900">Effective:</h6>
              <p className="text-sm text-slate-800">
                {announcement.start_date}
                {announcement.end_date ? ` To ${announcement.end_date}` : ''}
              </p>
            </div>
          )}

          <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 pb-2">
            <h6 className="text-sm font-semibold text-slate-900">Description:</h6>
            <div
              className="announcement-html text-sm text-slate-800"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          </div>
        </div>
      )}
    </OnScreenModal>
  )
}
